import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Modal } from '@/components/shared/Modal';
import { SharedButton } from '@/components/shared/Button';

import {
  isGLS,
  pickServiceGroupId,
  pickServiceGroupName,
  Subscription,
} from '@/types/subscriptions/subscription';

type Props = {
  subscription: Subscription;
  cancelSubscription: (id: number) => void;
};

export const CancelOrder: React.FC<Props> = ({
  subscription,
  cancelSubscription,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  const { t } = useTranslation();

  const id = pickServiceGroupId(subscription);
  // https://github.com/globis-org/glopla-dev-portal/issues/457#issuecomment-437284552
  if (id && isGLS(id)) {
    return null;
  }

  return (
    <>
      <SharedButton
        dataTest="do-cancel"
        dataGtm="cancel-subscription"
        onClick={handleOpen}
        disabled={!subscription.isUpdatable}
        color="red"
        buttonType="tight"
        fontSize="small"
        fontWeight="bold"
        type="submit"
      >
        {t('ui.action.cancelOrder')}
      </SharedButton>

      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={t('ui.confirm.cancelOrder')}
        buttons={
          <>
            <SharedButton
              dataTest="cancel"
              onClick={handleClose}
              color="gray"
              buttonType="tight"
              fontSize="small"
              fontWeight="bold"
            >
              {t('ui.action.cancel')}
            </SharedButton>

            <SharedButton
              data-test="execute"
              onClick={() => {
                cancelSubscription(subscription.id);
                handleClose();
              }}
              color="red"
              buttonType="tight"
              fontSize="small"
              fontWeight="bold"
            >
              {t('ui.action.executeCancelOrder')}
            </SharedButton>
          </>
        }
      >
        <Styled>
          <div className="subscription-detail-cancel-order-service-group-name">
            {`${pickServiceGroupName(subscription)}`}
          </div>
          <div className="subscription-detail-cancel-order-request-code">
            {`${t('models.subscription.requestCode')}： ${
              subscription.requestCode ? subscription.requestCode : ''
            }`}
          </div>
        </Styled>
      </Modal>
    </>
  );
};

const Styled = styled.div`
  .subscription-detail-cancel-order-service-group-name {
    font-weight: bold;
  }

  .subscription-detail-cancel-order-request-code {
    margin-top: 0.5em;
  }
`;
